import { initGameLoopManager } from '../../helpers/gameLoop';
import { Reel } from '../components/reel';
import { addTouchControl } from '../../helpers/touchSpinControll';
import { gameModuleStore } from '../store/gameModuleStore';
import { GAME_SPEED_TYPES } from '../../constants/gameConstants';
import { gameResourcesService } from './gameResourcesService';
import { handleSymbolAnimationPositionInMatrix } from '../../helpers/createWinLinesMatrix';
import { iconStates, specialIconKeys } from '../../constants/symbolsNames';
import {
  createCustomEases,
  handleReelIconStateInWinCase,
  handleWinLineBonePosition,
  setupPixiPlugins,
} from '../../helpers/gameModule';
import { gameConfigurationStore } from '../store/gameConfigurationStore';
import { skippablePromise } from '../../helpers/skippablePromise';
import { gameGlobalStates } from '../../constants/reelConstants';


class GameModuleService {
  reels = [];
  reelsContainer = null;
  winContainer = null;
  winFrameContainer = null;
  touchSpinScreen = null;
  spinDirection = 1;
  expandEndedColumns = [];
  winAnimations = [];
  generatorTicker = 0;
  stopNextReel = this.reelsStopGenerator();
  skippableAnimationPromise = [];

  constructor() {
    this.promiseControl = skippablePromise();
  }


  startSpin = () => {
    gameModuleStore.setGameGlobalState(gameGlobalStates.waiting);
    gameModuleStore.setCanTouchControl(false);
    gameModuleStore.setScatterCount('reset');
    clearTimeout(this.winLineTimeOutId);
    this.endAllWinAnimations();
    this.reels.forEach(reel => {
      reel.canStop = false;
      reel.startSpin();
      reel.isForceStop = false;
    });
  };

  startWildAnimation = async (reelsIndex, wins) => {
    this.reels.forEach((reel, i) => {
      if (reelsIndex[i] !== 0) this.skippableAnimationPromise.push(reel.startWildAnimation());
    });
    this.promiseControl.runPromise(this.skippableAnimationPromise, () => {
      if (wins.length) {
        this.startWinAnimation(wins);
      }
      this.skippableAnimationPromise = [];
    });
  };

  skipAnimations = () => {
    this.promiseControl.skipPromise();
  };

  stopAnimation = (matrix) => {
    gameModuleStore.setResultMatrix(matrix);
    this.checkHasJackpot(matrix);
    gameModuleStore.setResponseReady(true);
    if (gameModuleStore.spinSpeedType !== GAME_SPEED_TYPES.turbo) this.stopNextReel.next();
    this.reels.forEach((reel) => {
      reel.initFromResponseData(matrix);
      if (gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.turbo) reel.stopTurboReelAnimation();
    });
    gameModuleStore.setSpinDirection(1);

  };

  checkHasJackpot = (matrix) => {
    let jackpotSymbolsCount = 0;
    matrix.forEach((reel) => {
      reel.forEach((symble) => {
        if (symble === 400) jackpotSymbolsCount += 1;
      });
    });
    gameModuleStore.setHasJackpot(jackpotSymbolsCount);
  };

  * reelsStopGenerator() {
    let i = 0;
    while (true) {
      yield this.reels[i].canStop = true;
      i = (i + 1) % this.reels.length;
    }
  }

  updateReelsDirection(spinDirection) {
    this.spinDirection = spinDirection;
  }

  createReels = (reelsContainer, winContainer, touchSpinScreen, winFrameContainer, ticker) => {
    this.setReelsContainerAndWinContainer(reelsContainer, winContainer, winFrameContainer, touchSpinScreen);
    this.initReels(ticker);
  };

  setReelsContainerAndWinContainer(reelsContainer, winContainer, winFrameContainer, touchSpinScreen) {
    this.reelsContainer = reelsContainer;
    this.winContainer = winContainer;
    this.winFrameContainer = winFrameContainer;
    this.touchSpinScreen = touchSpinScreen;
  }

  initReels(ticker) {
    for (let reelIndex = 0; reelIndex < gameConfigurationStore.reelsParams.length; reelIndex++) {
      const reel = new Reel({
        index: reelIndex,
        elementId: `reel_${reelIndex}}`,
        reelsContainer: this.reelsContainer,
        slotWinLayer: this.winContainer,
        winFrameLayer: this.winFrameContainer,
        isLastReel: reelIndex === gameConfigurationStore.reelsParams.length - 1,
        stopNextReel: this.stopNextReel,
        reelParams: gameConfigurationStore.reelsParams,
      });
      this.reels.push(reel);
    }
    setupPixiPlugins();
    createCustomEases();
    initGameLoopManager(ticker);
    addTouchControl(this.touchSpinScreen, this.winContainer, this.reels);
  }

  startWinAnimation = async (wins) => {
    const scatterWinType = wins.find((el) => !el.line);
    const symbolWinType = wins.some(el => el.line);
    const { winLines, allLinesMatrix } = handleSymbolAnimationPositionInMatrix(wins);
    gameModuleStore.setGameGlobalState(gameGlobalStates.show);
    this.handleWinLineSound(winLines);
    if (symbolWinType || scatterWinType) {
      await Promise.all(this.allWinAnimation(winLines, allLinesMatrix));
      this.byLineWinAnimation(winLines);
    }
    gameModuleStore.animationEnd('allLineAnimationEnd', 0, scatterWinType && !symbolWinType);
  };

  handleWinLineSound(winLinesData) {
    const scattersWinLines = winLinesData.filter(
      (line) => line.winCard === specialIconKeys.scatter1,
    ).length;
    const symbol_1_2_WinLines = winLinesData.filter(
      (line) => line.winCard === specialIconKeys.symbol1 || line.winCard === specialIconKeys.symbol2,
    ).length;
    const symbol_3_4_WinLines = winLinesData.filter(
      (line) => line.winCard === specialIconKeys.symbol3 || line.winCard === specialIconKeys.symbol4,
    ).length;

    if (scattersWinLines) {
      gameModuleStore.soundService.play('WinningLineSound4');
      return;
    }
    if (symbol_1_2_WinLines) {
      gameModuleStore.soundService.play('WinningLineSound3');
      return;
    }
    if (symbol_3_4_WinLines) {
      gameModuleStore.soundService.play('WinningLineSound2');
      return;
    }

    gameModuleStore.soundService.play('WinningLineSound1');
  }

  handleScatterWin(iconNumber) {
    this.reels.forEach((reel) => {
      reel.icons.forEach((icon) => {
        if (icon.iconNumber === iconNumber) {
          icon.changeState(iconStates.win, true);
          icon.changeBoardState(iconStates.win);
        } else {
          icon.changeState(iconStates.noWin);
          icon.changeBoardState(iconStates.noWin);
        }
      });
    });
  }

  allWinAnimation = (winLines, allLinesMatrix) => {
    const animationPromises = [];
    gameModuleStore.animationEnd('allLineAnimationStart');
    this.reels.forEach((reel) => {
      animationPromises.push(...reel.startWinAnimation(handleReelIconStateInWinCase(allLinesMatrix, reel)));
    });
    winLines.forEach(({ line, lines }) => {
      const animationName = 'animation';
      if (line) {
        this.reels.forEach((reel) => {
          const index = reel.index;
          gameResourcesService.allWinLinesBone[line - 1][index].y = handleWinLineBonePosition(lines, reel);
        });
        const spineLine = gameResourcesService.allLinesSpine[line - 1];
        spineLine.visible = true;
        spineLine.play(animationName);
        gameModuleStore.setWinAnimations(spineLine);
        animationPromises.push(
          new Promise((animResolve) => {
            const onComplete = () => {
              spineLine.visible = false;
              spineLine.removeListener('complete', onComplete, null, animationName);
              animResolve();
            };
            spineLine.addListener('complete', onComplete, null, animationName);
          }),
        );
      }

    });

    return animationPromises;
  };

  startJackpotSymblsAnimation = () => {
    this.reels.forEach((reel) => {
      reel.startJackpotSymblsAnimation();
    });
  };

  async byLineWinAnimation(winLinesData) {
    let start = 0;
    while (gameModuleStore.gameGlobalState === gameGlobalStates.show) {
      const winLine = winLinesData[start];
      const { line, lines } = winLine;
      const animations = [];
      const lineIndex = line - 1;
      const animationName = 'animation';

      for (let i = 0; i < this.reels.length; i++) {
        const column = this.reels[i];
        const { visibleRows } = gameConfigurationStore.reelsParams[i];
        for (let j = 0; j < visibleRows; j++) {
          const icon = column.icons[j + visibleRows];
          if (lines[j][i] === 1) {
            icon.changeBoardState(iconStates.win);
          } else {
            icon.changeBoardState(iconStates.noWin);
          }
          if (lines[j][i] && line) {
            gameResourcesService.allWinLinesBone[lineIndex][i].y = handleWinLineBonePosition(lines, this.reels[i]);
          }
        }
      }

      gameModuleStore.animationEnd('byLineAnimation', winLine);
      if (gameConfigurationStore.isReplay && start === winLinesData.length - 1) {
        gameModuleStore.animationEnd('byLineAnimationEnd');
      }
      if (line) {
        const spineLine = gameResourcesService.allLinesSpine[lineIndex];
        spineLine.play(animationName);
        animations.push(spineLine);
        gameModuleStore.setWinAnimations(spineLine);
        spineLine.visible = true;
        await Promise.all(
          animations.map(
            (animation) =>
              new Promise((animResolve) => {
                if (animation?.animationType) {
                  animation.icon.addListener(
                    'complete',
                    () => animResolve(),
                    null,
                    animation.currentState,
                  );
                } else {
                  animation.addListener('complete', () => {
                    animResolve(), spineLine.visible = false;
                  }, null, animationName);
                }
              }),
          ),
        );
      } else {
        await new Promise((resolve) => {
          this.winLineTimeOutId = setTimeout(resolve, 1000);
        });
      }
      start = (start + 1) % winLinesData.length;
    }
  }

  endAllWinAnimations = () => {
    gameModuleStore.winAnimations.forEach(animation => {
      if (animation.icon) {
        animation.icon.goToAnimationEnd(animation.currentState);
      } else {
        animation.goToAnimationEnd(animation.state.tracks[0].animation.name);
      }
    });
    gameModuleStore.setWinAnimations();
  };

}

export const gameModuleService = new GameModuleService();
